import { CardProfile } from '../../types/CardProfile';
import {
	AddOrderPayload,
	AddOrderReturn,
	AddSalePayload,
	EditOrderPayload,
	EditOrderReturn,
	EditSalePayload,
	PatchSalePayload,
	GETOrdersResponse,
	GETSalesResponse,
} from '../../types/TixAPITypes';
import { TixBought, TixEvent, TixOrder, TixSale } from '../../types/TixTypes';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type QueryParams = Record<string, string | number | boolean | undefined>;
export type Params = Record<string, string | number>;

type BuildParams = {
	queryParams?: QueryParams;
	userSession?: boolean;
	sessionKey?: string;
	body?: Record<string, string | boolean | Array<unknown> | number | object | null>;
	params?: Params;
};

type BuildParameters = {
	url: string;
	method: HttpMethod;
	queryParams?: QueryParams;
	body?: Record<string, string | boolean | Array<unknown> | number | object | null>;
	userSession?: boolean;
	sessionKey?: string;
};

// NOTE: _result is a phantom field used for the API_EndpointResult
export type EndpointDefinition<TParams extends BuildParams, TResult> = {
	build: (p: TParams) => BuildParameters;
	_result?: TResult;
};

export type API_EndpointKey = keyof typeof apiEndpoints;

export type API_EndpointParams<K extends API_EndpointKey> = Parameters<(typeof apiEndpoints)[K]['build']>[0];

export type API_EndpointResult<K extends API_EndpointKey> = (typeof apiEndpoints)[K] extends { _result?: infer R }
	? R
	: never;

export type APIResponse<T> = Promise<APIResponseType<T>>;

export type APIResponseType<T> =
	| { success: true; data: T; dataRaw: string; code?: number }
	| { success: false; error: string; code?: number };

function defineEndpoint<TResult, TExtra extends object>() {
	return (def: {
		build: (p: BuildParams & TExtra) => BuildParameters;
	}): EndpointDefinition<BuildParams & TExtra, TResult> => def;
}

export function getEndpoint<K extends API_EndpointKey>(key: K): (typeof apiEndpoints)[K] {
	return apiEndpoints[key];
}

export function callEndpoint<K extends API_EndpointKey>(...[key, params]: [K, API_EndpointParams<K>]) {
	return (apiEndpoints[key].build as unknown as (p: API_EndpointParams<K>) => BuildParameters)(params);
}

export const apiEndpoints = {
	'tix.get-orders': defineEndpoint<GETOrdersResponse, {}>()({
		build: () => ({
			url: `/tix/order`,
			method: 'GET',
			userSession: true,
		}),
	}),

	'tix.post-order': defineEndpoint<AddOrderReturn, { body: AddOrderPayload }>()({
		build: ({ body }) => ({
			url: `/tix/order`,
			method: 'POST',
			body,
			userSession: true,
		}),
	}),

	'tix.put-order': defineEndpoint<EditOrderReturn, { body: EditOrderPayload }>()({
		build: ({ body }) => ({
			url: `/tix/order`,
			method: 'PUT',
			body,
			userSession: true,
		}),
	}),

	'tix.delete-order': defineEndpoint<undefined, { params: { orderId: TixOrder['id'] } }>()({
		build: ({ params }) => ({
			url: `/tix/order/${params.orderId}`,
			method: 'DELETE',
			userSession: true,
		}),
	}),

	'tix.delete-seats': defineEndpoint<
		undefined,
		{ params: { eventId: TixEvent['id'] }; body: { seatIds: TixBought['id'][] } }
	>()({
		build: ({ params, body }) => ({
			url: `/tix/event/${params.eventId}/seats`,
			method: 'DELETE',
			body: body,
			userSession: true,
		}),
	}),

	'tix.get-sales': defineEndpoint<GETSalesResponse, { queryParams: { quaterId: string } }>()({
		build: ({ queryParams }) => ({
			url: `/tix/sale`,
			method: 'GET',
			userSession: true,
			queryParams: queryParams,
		}),
	}),

	'tix.post-sale': defineEndpoint<TixSale, { body: AddSalePayload }>()({
		build: ({ body }) => ({
			url: `/tix/sale`,
			method: 'POST',
			body,
			userSession: true,
		}),
	}),

	'tix.put-sale': defineEndpoint<TixSale, { body: EditSalePayload }>()({
		build: ({ body }) => ({
			url: `/tix/sale`,
			method: 'PUT',
			body,
			userSession: true,
		}),
	}),

	'tix.patch-sale': defineEndpoint<TixSale, { body: PatchSalePayload }>()({
		build: ({ body }) => ({
			url: `/tix/sale`,
			method: 'PATCH',
			body,
			userSession: true,
		}),
	}),

	'tix.delete-sale': defineEndpoint<undefined, { params: { saleId: TixSale['id'] } }>()({
		build: ({ params }) => ({
			url: `/tix/sale/${params.saleId}`,
			method: 'DELETE',
			userSession: true,
		}),
	}),
	'tix.import': defineEndpoint<{ unsavedOrders: { id: string; error: string }[] }, { body: { importCSV: string } }>()(
		{
			build: ({ body }) => ({
				url: `/tix/import`,
				method: 'POST',
				body,
				userSession: true,
			}),
		},
	),
	'tix.import-revolut-payments': defineEndpoint<
		{ skippedOrders: { amount: number; date: string; currency: string; email: string }[] },
		{ body: { importCSV: string } }
	>()({
		build: ({ body }) => ({
			url: `/tix/import/import-revolut-payments`,
			method: 'POST',
			body,
			userSession: true,
		}),
	}),
	'tix.import-revolut-payments-old': defineEndpoint<
		{ skippedOrders: { amount: number; date: string; currency: string; email: string }[] },
		{ body: { importCSV: string } }
	>()({
		build: ({ body }) => ({
			url: `/tix/import/import-revolut-payments-old`,
			method: 'POST',
			body,
			userSession: true,
		}),
	}),
	'tix.import-payments': defineEndpoint<
		{ unsavedSales: { id: string; error: string }[] },
		{ body: { importCSV: string } }
	>()({
		build: ({ body }) => ({
			url: `/tix/import/payments`,
			method: 'POST',
			body,
			userSession: true,
		}),
	}),

	'tix.import-payments-native': defineEndpoint<
		{ unsavedSales: { id: string; error: string }[] },
		{ body: { importCSV: string } }
	>()({
		build: ({ body }) => ({
			url: `/tix/import/payments-native`,
			method: 'POST',
			body,
			userSession: true,
		}),
	}),
	'tix.quater-stats-csv': defineEndpoint<{}, { params: { quaterId: string } }>()({
		build: ({ params }) => ({
			url: `/tix/quater/${params.quaterId}/stats/csv`,
			method: 'GET',
			userSession: true,
		}),
	}),
	'tix.quater-events-csv': defineEndpoint<{}, { params: { quaterId: string } }>()({
		build: ({ params }) => ({
			url: `/tix/quater/${params.quaterId}/events/csv`,
			method: 'GET',
			userSession: true,
		}),
	}),

	'card.list': defineEndpoint<{ cards: CardProfile[] }, {}>()({
		build: ({ body }) => ({
			url: `/cards`,
			method: 'GET',
			body,
			userSession: true,
		}),
	}),
	'card.add': defineEndpoint<{ cards: CardProfile[] }, { body: { importCSV: string } }>()({
		build: ({ body }) => ({
			url: `/cards`,
			method: 'POST',
			body,
			userSession: true,
		}),
	}),
	'card.delete': defineEndpoint<{}, { body: { cardIds: CardProfile['id'][] } }>()({
		build: ({ body }) => ({
			url: `/cards`,
			method: 'DELETE',
			body,
			userSession: true,
		}),
	}),

	'auth.login': defineEndpoint<{}, { body: { key: string; password: string } }>()({
		build: ({ body }) => ({
			url: `/auth/login`,
			method: 'POST',
			body,
		}),
	}),
} as const;
